import { toggleJobTask, uploadJobFile, type JobAttachment } from '../api/jobWorkflow'
import { isFieldGpsError } from './fieldGpsGate'
import {
  listAllQueuedPhotos,
  removeQueuedPhoto,
  type QueuedJobPhotoWithBlob,
} from './offlinePhotoQueue'

export function isLikelyOfflineError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true
  if (isFieldGpsError(err)) return false
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase()
  return /failed to fetch|network|offline|load failed|networkerror|fetch error|timeout|connection|aborted/.test(
    msg
  )
}

export function queuedPhotoToAttachment(item: QueuedJobPhotoWithBlob): JobAttachment {
  const blobUrl = URL.createObjectURL(item.blob)
  return {
    id: item.id,
    job_id: item.jobId,
    job_task_id: item.jobTaskId,
    file_url: blobUrl,
    file_name: item.fileName,
    mime_type: item.mimeType,
    attachment_type: item.attachmentType,
    capture_source: item.captureSource,
    created_at: item.createdAt,
  }
}

let syncing = false

/** Upload queued photos when connectivity returns. Stops on first offline failure. */
export async function syncOfflinePhotoQueue(): Promise<{ synced: number; remaining: number }> {
  if (syncing || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    const remaining = (await listAllQueuedPhotos()).length
    return { synced: 0, remaining }
  }

  syncing = true
  let synced = 0
  try {
    const items = await listAllQueuedPhotos()
    for (const item of items) {
      try {
        const file = new File([item.blob], item.fileName, {
          type: item.mimeType || 'image/jpeg',
        })
        await uploadJobFile(
          item.jobId,
          file,
          item.attachmentType,
          undefined,
          item.captureSource,
          item.jobTaskId
        )
        if (item.shouldAutoCheck && item.jobTaskId) {
          await toggleJobTask(item.jobTaskId, true)
        }
        await removeQueuedPhoto(item.id)
        synced++
      } catch (e) {
        if (isLikelyOfflineError(e)) break
        // Leave non-network failures in queue for a later retry / manual fix.
      }
    }
  } finally {
    syncing = false
  }

  const remaining = (await listAllQueuedPhotos()).length
  return { synced, remaining }
}
